import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Box, CircularProgress, Button, Typography } from "@mui/material";
import { TrashIcon, ResendIcon, EditProfileIcon } from "../../components/Icons";
import { Layout } from "../../components/layout";
import { Search } from "../../components/Search";
import DataTable, { ColumnConfig } from "../../components/DataTable";
import PersonsInfo from "../../components/PersonsInfo";
import { RightArrowIcon } from "../../components/Icons";
import {
  useModal,
  ConfirmActionModalContent,
  AssignPatientModalContent,
  ReassignPatientModalContent,
} from "../../components/Modal";
import {
  usersService,
  UserDetails,
  UserStatus,
  Specialty,
} from "../../services/usersService";
import { UserRole } from "../../constants";
import { patientsService, Patient } from "../../services/patientsService";
import { selectUser } from "../../store/selectors/authSelectors";
import { colors } from "../../theme/colors";
import lockedFilesImage from "../../assets/images/locked-files.jpg";
import signatureImage from "../../assets/images/signature.jpg";
import {
  StatusChip,
  PageContainer,
  PageHeader,
  PageTitleContainer,
  HeaderActions,
  CredentialsGrid,
  CredentialItem,
  CredentialLabel,
  CredentialValue,
  SectionHeader,
  SectionTitle,
  SectionActions,
  NoDataText,
  ResendButton,
  SuspendButton,
  ReactivateButton,
  SearchContainer,
  ChangePasswordButton,
} from "./UserProfilePage.styled";

const PATIENTS_PER_PAGE = 5;

interface PatientRow extends Record<string, unknown> {
  id: number;
  externalId: string;
  fullName: string;
  dateOfBirth: string;
  email: string;
  phone: string;
  recentTreatmentPlanId: number | null;
  recentTreatmentPlanName: string | null;
}

interface UserProfilePageProps {
  isCurrentUser?: boolean;
}

const UserProfilePage: React.FC<UserProfilePageProps> = ({
  isCurrentUser = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { openModal, closeModal } = useModal();
  const currentUser = useSelector(selectUser);

  const [user, setUser] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(!isCurrentUser);
  const [error, setError] = useState<string | null>(null);
  const fetchInitiatedRef = useRef(false);

  const [patients, setPatients] = useState<Patient[]>([]);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [patientsTotal, setPatientsTotal] = useState(0);
  const [patientSearch, setPatientSearch] = useState("");
  const [patientSortField, setPatientSortField] = useState<string>("fullName");
  const [patientSortDirection, setPatientSortDirection] = useState<
    "asc" | "desc"
  >("asc");
  const [patientPage, setPatientPage] = useState(1);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);

  // Fetch specialties from API
  useEffect(() => {
    usersService.getSpecialties().then(setSpecialties).catch(console.error);
  }, []);

  // For current user, use data from Redux
  useEffect(() => {
    if (isCurrentUser && currentUser) {
      setUser(currentUser as unknown as UserDetails);
      setLoading(false);
    }
  }, [isCurrentUser, currentUser]);

  // For other users, fetch by ID
  useEffect(() => {
    if (isCurrentUser || !id || fetchInitiatedRef.current) return;

    fetchInitiatedRef.current = true;
    setLoading(true);

    usersService
      .getUserById(parseInt(id))
      .then((data) => {
        setUser(data);
      })
      .catch((err) => {
        console.error("Error fetching user:", err);
        setError(t("userProfile.loadError"));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, t, isCurrentUser]);

  const userId = isCurrentUser ? user?.id : id ? parseInt(id) : null;

  const fetchPatients = useCallback(async () => {
    if (!userId || !user || user.roleId !== UserRole.Provider) return;

    setPatientsLoading(true);
    try {
      const orderBy =
        patientSortField === "fullName"
          ? "Name"
          : patientSortField === "dateOfBirth"
            ? "DateOfBirth"
            : "CreatedAt";
      const response = await patientsService.getPatients({
        providerId: [userId],
        search: patientSearch || undefined,
        orderBy,
        order: patientSortDirection === "asc" ? 0 : 1,
        start: (patientPage - 1) * PATIENTS_PER_PAGE,
        limit: PATIENTS_PER_PAGE,
        total: true,
      });
      setPatients(response.data);
      setPatientsTotal(response.total);
    } catch (err) {
      console.error("Error fetching patients:", err);
    } finally {
      setPatientsLoading(false);
    }
  }, [
    userId,
    user,
    patientSearch,
    patientSortField,
    patientSortDirection,
    patientPage,
  ]);

  useEffect(() => {
    if (user && user.roleId === UserRole.Provider) {
      fetchPatients();
    }
  }, [user, fetchPatients]);

  const formatPhone = (phone: string): string => {
    if (!phone) return "-";
    const digits = phone.replace(/\D/g, "");
    const localDigits =
      digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
    if (localDigits.length === 10) {
      return `+1 (${localDigits.slice(0, 3)}) ${localDigits.slice(3, 6)}-${localDigits.slice(6)}`;
    }
    return phone;
  };

  const formatDate = (isoDate: string): string => {
    if (!isoDate) return "-";
    const date = new Date(isoDate);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const getStatusType = (
    status: number,
  ): "active" | "inactive" | "invited" | "suspended" => {
    switch (status) {
      case UserStatus.Active:
        return "active";
      case UserStatus.Inactive:
        return "inactive";
      case UserStatus.Invited:
        return "invited";
      case UserStatus.Suspended:
        return "suspended";
      default:
        return "active";
    }
  };

  const getStatusLabel = (status: number): string => {
    switch (status) {
      case UserStatus.Active:
        return t("userManagement.status.active");
      case UserStatus.Inactive:
        return t("userManagement.status.inactive");
      case UserStatus.Invited:
        return t("userManagement.status.invited");
      case UserStatus.Suspended:
        return t("userManagement.status.suspended");
      default:
        return "-";
    }
  };

  const getPageTitle = (roleId: number): string => {
    if (isCurrentUser) {
      return t("myProfile.title");
    }
    switch (roleId) {
      case UserRole.Provider:
        return t("userProfile.providerProfile");
      case UserRole.Assistant:
        return t("userProfile.assistantProfile");
      case UserRole.Manager:
        return t("userProfile.managerProfile");
      default:
        return t("userProfile.providerProfile");
    }
  };

  const specialtyLabel = useMemo(() => {
    if (!user?.specialtyIds || user.specialtyIds.length === 0) return "-";
    const names = user.specialtyIds
      .map((id) => specialties.find((spec) => spec.id === id)?.name)
      .filter(Boolean);
    return names.length > 0 ? names.join(", ") : "-";
  }, [user?.specialtyIds, specialties]);

  const patientRows: PatientRow[] = useMemo(() => {
    return patients.map((p) => ({
      id: p.id,
      externalId: p.externalId,
      fullName: `${p.firstName} ${p.lastName}`.trim(),
      dateOfBirth: p.dateOfBirth,
      email: p.email || "-",
      phone: p.phone,
      recentTreatmentPlanId: p.latestTreatmentPlanId || null,
      recentTreatmentPlanName: p.latestTreatmentPlanName || null,
    }));
  }, [patients]);

  const handleEditClick = () => {
    if (isCurrentUser) {
      navigate("/profile/edit");
    } else {
      navigate(`/user-management/${id}/edit`);
    }
  };

  const handleChangePassword = () => {
    // TODO: Implement change password modal
    console.log("Change password clicked");
  };

  const handleResendInvite = () => {
    if (!user) return;
    openModal({
      id: "resend-invite-modal",
      content: (
        <ConfirmActionModalContent
          title={t("userProfile.resendInviteConfirm.title")}
          message={t("userProfile.resendInviteConfirm.message")}
          confirmText={t("userProfile.resendInviteConfirm.confirm")}
          onConfirm={async () => {
            await usersService.resendInvite(user.id);
            closeModal("resend-invite-modal");
            fetchInitiatedRef.current = false;
            setLoading(true);
            usersService
              .getUserById(user.id)
              .then(setUser)
              .finally(() => setLoading(false));
          }}
          onCancel={() => closeModal("resend-invite-modal")}
        />
      ),
      maxWidth: 480,
    });
  };

  const openReassignPatientModal = (suspendedProviderId: number) => {
    openModal({
      id: "reassign-patient-modal",
      showCloseButton: true,
      containerPadding: "60px 90px 90px 90px",
      content: (
        <ReassignPatientModalContent
          suspendedProviderId={suspendedProviderId}
          onReassign={() => {
            closeModal("reassign-patient-modal");
            fetchInitiatedRef.current = false;
            setLoading(true);
            usersService
              .getUserById(suspendedProviderId)
              .then(setUser)
              .finally(() => setLoading(false));
          }}
          onCancel={() => {
            closeModal("reassign-patient-modal");
            fetchInitiatedRef.current = false;
            setLoading(true);
            usersService
              .getUserById(suspendedProviderId)
              .then(setUser)
              .finally(() => setLoading(false));
          }}
        />
      ),
      maxWidth: 636,
    });
  };

  const handleSuspendUser = () => {
    if (!user) return;
    openModal({
      id: "suspend-user-modal",
      showCloseButton: true,
      containerPadding: "60px 90px 90px 90px",
      content: (
        <ConfirmActionModalContent
          icon={lockedFilesImage}
          title={t("userProfile.suspendConfirm.title")}
          message={t("userProfile.suspendConfirm.message")}
          confirmText={t("userProfile.suspendConfirm.confirm")}
          confirmButtonColor="error"
          onConfirm={async () => {
            closeModal("suspend-user-modal");
            if (user.roleId === UserRole.Provider) {
              let hasPatients = false;
              try {
                const { data } = await patientsService.getPatients({ providerId: [user.id], limit: 1 });
                hasPatients = data.length > 0;
              } catch {
                hasPatients = false;
              }
              if (hasPatients) {
                openReassignPatientModal(user.id);
              } else {
                await usersService.suspendUser(user.id);
                fetchInitiatedRef.current = false;
                setLoading(true);
                usersService
                  .getUserById(user.id)
                  .then(setUser)
                  .finally(() => setLoading(false));
              }
            } else {
              await usersService.suspendUser(user.id);
              fetchInitiatedRef.current = false;
              setLoading(true);
              usersService
                .getUserById(user.id)
                .then(setUser)
                .finally(() => setLoading(false));
            }
          }}
          onCancel={() => closeModal("suspend-user-modal")}
        />
      ),
      maxWidth: 636,
    });
  };

  const handleReactivateUser = () => {
    if (!user) return;
    openModal({
      id: "reactivate-user-modal",
      content: (
        <ConfirmActionModalContent
          title={t("userProfile.reactivateConfirm.title")}
          message={t("userProfile.reactivateConfirm.message")}
          confirmText={t("userProfile.reactivateConfirm.confirm")}
          onConfirm={async () => {
            await usersService.reactivateUser(user.id);
            closeModal("reactivate-user-modal");
            fetchInitiatedRef.current = false;
            setLoading(true);
            usersService
              .getUserById(user.id)
              .then(setUser)
              .finally(() => setLoading(false));
          }}
          onCancel={() => closeModal("reactivate-user-modal")}
        />
      ),
      maxWidth: 480,
    });
  };

  const handlePatientClick = useCallback(
    (patientId: number) => {
      navigate(`/patients/${patientId}`);
    },
    [navigate],
  );

  const handleTreatmentPlanClick = useCallback(
    (planId: number) => {
      navigate(`/treatment-plan/${planId}`);
    },
    [navigate],
  );

  const handleViewAllPatients = () => {
    navigate(`/patients?providerId=${userId}`);
  };

  const handleAssignPatient = () => {
    if (!userId) return;
    openModal({
      id: "assign-patient-modal",
      showCloseButton: true,
      content: (
        <AssignPatientModalContent
          providerId={userId}
          onAssign={async (patientIds: number[]) => {
            // Merge already-loaded patients from the view with newly selected ones
            // PUT /providers/{id}/patients expects the full list
            const existingIds = patients.map((p) => p.id);
            const mergedIds = Array.from(new Set([...existingIds, ...patientIds]));
            await usersService.assignPatients(userId, mergedIds);
            closeModal("assign-patient-modal");
            fetchPatients();
          }}
          onCancel={() => closeModal("assign-patient-modal")}
        />
      ),
      maxWidth: 580,
    });
  };

  const handleUnassignPatient = useCallback(
    (patient: PatientRow) => {
      if (!user) return;
      openModal({
        id: "unassign-patient-modal",
        showCloseButton: true,
        content: (
          <ConfirmActionModalContent
            icon={signatureImage}
            title={t("userProfile.unassignPatientConfirm.title")}
            message={t("userProfile.unassignPatientConfirm.message")}
            confirmText={t("userProfile.unassignPatientConfirm.confirm")}
            confirmButtonColor="error"
            onConfirm={async () => {
              await usersService.unassignPatient(user.id, patient.id);
              closeModal("unassign-patient-modal");
              fetchPatients();
            }}
            onCancel={() => closeModal("unassign-patient-modal")}
          />
        ),
        maxWidth: 620,
      });
    },
    [user, openModal, closeModal, fetchPatients, t],
  );

  const handleSort = (field: string, direction: "asc" | "desc") => {
    setPatientSortField(field);
    setPatientSortDirection(direction);
  };

  const handlePageChange = (page: number) => {
    setPatientPage(page);
  };

  // Provider viewing their own profile cannot unassign patients
  const canUnassignPatients = !(
    isCurrentUser && currentUser?.roleId === UserRole.Provider
  );

  const patientColumns: ColumnConfig<PatientRow>[] = useMemo(() => {
    const columns: ColumnConfig<PatientRow>[] = [
      {
        key: "fullName",
        title: t("userProfile.patientName"),
        sortable: true,
        render: (value: unknown, row: PatientRow) => (
          <Typography
            className="clickable"
            onClick={() => handlePatientClick(row.id)}
          >
            {String(value)}
          </Typography>
        ),
      },
      {
        key: "dateOfBirth",
        title: t("userProfile.dateOfBirthShort"),
        sortable: true,
        render: (value: unknown) => (
          <Typography>{formatDate(String(value))}</Typography>
        ),
      },
      {
        key: "email",
        title: t("userProfile.contactEmail"),
      },
      {
        key: "phone",
        title: t("userProfile.contactPhone"),
        render: (value: unknown) => (
          <Typography>{formatPhone(String(value))}</Typography>
        ),
      },
      {
        key: "recentTreatmentPlanName",
        title: t("userProfile.recentTreatmentPlan"),
        render: (value: unknown, row: PatientRow) => {
          if (row.recentTreatmentPlanId && value) {
            return (
              <Typography
                className="clickable"
                onClick={() =>
                  handleTreatmentPlanClick(row.recentTreatmentPlanId!)
                }
              >
                {String(value)}
              </Typography>
            );
          }
          return <Typography>{t("userProfile.noPlanAvailable")}</Typography>;
        },
      },
    ];

    // Only show actions column if user can unassign patients
    if (canUnassignPatients) {
      columns.push({
        key: "actions",
        title: t("userProfile.actions"),
        align: "center",
        render: (_value: unknown, row: PatientRow) => (
          <Button
            size="small"
            sx={{
              minWidth: "auto",
              padding: "4px",
              color: colors.status.error,
            }}
            onClick={() => handleUnassignPatient(row)}
          >
            <TrashIcon />
          </Button>
        ),
      });
    }

    return columns;
  }, [
    t,
    canUnassignPatients,
    handlePatientClick,
    handleTreatmentPlanClick,
    handleUnassignPatient,
  ]);

  if (loading) {
    return (
      <Layout>
        <PageContainer>
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        </PageContainer>
      </Layout>
    );
  }

  if (error || !user) {
    return (
      <Layout>
        <PageContainer>
          <NoDataText>{error || t("userProfile.notFound")}</NoDataText>
        </PageContainer>
      </Layout>
    );
  }

  const isProvider = user.roleId === UserRole.Provider;
  // Provider viewing their own profile has limited actions
  const isProviderOwnProfile = isCurrentUser && isProvider;
  // Can assign patients only for Active providers
  const canAssignPatients =
    isProvider &&
    !isProviderOwnProfile &&
    user.status === UserStatus.Active;

  return (
    <Layout>
      <PageContainer>
        <PageHeader>
          <PageTitleContainer>
            <Typography variant="h1">{getPageTitle(user.roleId)}</Typography>
            {!isCurrentUser && (
              <StatusChip
                label={getStatusLabel(user.status)}
                status={getStatusType(user.status)}
                size="small"
              />
            )}
          </PageTitleContainer>
          <HeaderActions>
            {isCurrentUser ? (
              <>
                {/* Provider cannot change password or manage procedure duration */}
                {!isProviderOwnProfile && (
                  <>
                    <ChangePasswordButton
                      variant="contained"
                      onClick={handleChangePassword}
                    >
                      {t("myProfile.changePassword")}
                    </ChangePasswordButton>
                  </>
                )}
                <Button
                  variant="outlined"
                  startIcon={<EditProfileIcon />}
                  onClick={handleEditClick}
                >
                  {t("myProfile.editProfile")}
                </Button>
              </>
            ) : (
              <>
                {user.status === UserStatus.Invited && (
                  <ResendButton
                    variant="outlined"
                    startIcon={<ResendIcon />}
                    onClick={handleResendInvite}
                  >
                    {t("userProfile.resendInvite")}
                  </ResendButton>
                )}
                <Button
                  variant="outlined"
                  startIcon={<EditProfileIcon />}
                  onClick={handleEditClick}
                >
                  {t("userProfile.editProfile")}
                </Button>
                {user.status === UserStatus.Suspended ? (
                  <ReactivateButton
                    variant="contained"
                    onClick={handleReactivateUser}
                  >
                    {t("userProfile.reactivate")}
                  </ReactivateButton>
                ) : (
                  <SuspendButton
                    variant="contained"
                    onClick={handleSuspendUser}
                  >
                    {t("userProfile.suspendUser")}
                  </SuspendButton>
                )}
              </>
            )}
          </HeaderActions>
        </PageHeader>

        <PersonsInfo
          persons={[
            {
              name: `${user.firstName} ${user.lastName}`,
              role: user.roleTitle,
              avatarUrl: user.avatarUrl,
              dateOfBirth: formatDate(user.dateOfBirth),
              phone: formatPhone(user.phoneNumber),
              email: user.email,
            },
          ]}
          avatarSize={80}
          size="large"
          margin="0 0 40px 0"
        />

        <CredentialsGrid>
          <CredentialItem>
            <CredentialLabel>
              {t("userProfile.dentalLicenseNumber")}
            </CredentialLabel>
            <CredentialValue>{user.dentalLicenseNumber || "-"}</CredentialValue>
          </CredentialItem>
          <CredentialItem>
            <CredentialLabel>{t("userProfile.deaNumber")}</CredentialLabel>
            <CredentialValue>{user.deaNumber || "-"}</CredentialValue>
          </CredentialItem>
              <CredentialItem>
                <CredentialLabel>
                  {t("userProfile.specialtyLicenseNumber")}
                </CredentialLabel>
                <CredentialValue>
                  {user.specialtyLicenseNumber || "-"}
                </CredentialValue>
              </CredentialItem>
          {isProvider && (
              <CredentialItem>
                <CredentialLabel>{t("userProfile.specialty")}</CredentialLabel>
                <CredentialValue>{specialtyLabel}</CredentialValue>
              </CredentialItem>
          )}
          <CredentialItem>
            <CredentialLabel>{t("userProfile.npiNumber")}</CredentialLabel>
            <CredentialValue>{user.npiNumber || "-"}</CredentialValue>
          </CredentialItem>
        </CredentialsGrid>

        {/* Provider's own profile doesn't show patients table; also hidden for non-Active providers */}
        {isProvider && !isProviderOwnProfile && user.status === UserStatus.Active && (
          <>
            <SectionHeader>
              <SectionTitle>{t("userProfile.assignedPatients")}</SectionTitle>
              <SectionActions>
                <SearchContainer>
                  <Search
                    width={320}
                    value={patientSearch}
                    onChange={(e) => setPatientSearch(e.target.value)}
                    placeholder={t("userProfile.searchPatients")}
                  />
                </SearchContainer>
                {canAssignPatients && (
                <Button
                  variant="contained"
                  onClick={handleAssignPatient}
                  sx={{
                    backgroundColor: colors.amber.main,
                    "&:hover": { backgroundColor: colors.amber.dark },
                  }}
                >
                  + {t("userProfile.assignPatient")}
                </Button>
                )}
                <Button
                  variant="outlined"
                  onClick={handleViewAllPatients}
                  endIcon={<RightArrowIcon fill={colors.primary.main} />}
                >
                  {t("userProfile.viewAllAssignedPatients")}
                </Button>
              </SectionActions>
            </SectionHeader>

            <DataTable<PatientRow>
              columns={patientColumns}
              data={patientRows}
              loading={patientsLoading}
              sortField={patientSortField}
              sortDirection={patientSortDirection}
              onSort={handleSort}
              pageSize={PATIENTS_PER_PAGE}
              currentPage={patientPage}
              totalCount={patientsTotal}
              onPageChange={handlePageChange}
              emptyMessage={t("userProfile.noPatientsAssigned")}
            />
          </>
        )}
      </PageContainer>
    </Layout>
  );
};

export default UserProfilePage;
